import { create } from 'zustand';
import { PaginationType } from '../types/pagination';

import {
  deleteSchedule,
  fetchScheduleList,
  FetchSchedulerListRequest,
  postSchedule,
  PostScheduleRequest,
  putSchedule,
  PutScheduleRequest,
  SchedulerType,
} from './api/scheduler-api';
import { useAlertStore } from './use-alert-store';

interface Store {
  isLoading?: boolean;
  list?: SchedulerType[];
  pagination?: PaginationType;
  fetchList: (params: Partial<FetchSchedulerListRequest>) => Promise<void>;
  postScheduler: (params: Partial<PostScheduleRequest>) => Promise<void>;
  putScheduler: (params: Partial<PutScheduleRequest>) => Promise<void>;
  deleteScheduler: (id: string) => Promise<void>;
}

export const useSchedulerStore = create<Store>((set, get) => ({
  fetchList: async (params) => {
    set({ isLoading: true });
    try {
      const { schedule, ...other } = await fetchScheduleList(params);

      const isInfinite = !!params.infinite;
      const currentList = get().list;
      const updatedList = isInfinite && currentList ? currentList.concat(schedule) : schedule;

      set({ list: updatedList, pagination: other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить запланированные задачи', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  postScheduler: async (params) => {
    try {
      set({
        isLoading: true,
      });

      const resp = await postSchedule(params);
      const scheduler = resp.schedule_item;
      set((store) => ({ ...store, list: [scheduler, ...(store.list || [])] }));
      useAlertStore.getState().add({ label: 'Задача добавлена в планировщик', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось добавить задачу в планировщик', variant: 'danger' });
      throw new Error();
    } finally {
      set({ isLoading: false });
    }
  },
  putScheduler: async (params) => {
    try {
      set({
        isLoading: true,
      });
      const response = await putSchedule(params);
      const task = response.schedule_item;
      const updated = get()?.list?.map((item) => (item.id === task.id ? task : item));
      set({ list: updated });
      useAlertStore.getState().add({ label: 'Задача успешно обновлена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось обновить задачу', variant: 'danger' });
      throw new Error();
    } finally {
      set({ isLoading: false });
    }
  },
  deleteScheduler: async (id) => {
    try {
      set({
        isLoading: true,
      });

      await deleteSchedule(id);

      set((store) => ({
        ...store,
        list: store.list!.filter((item) => item.id !== +id),
      }));

      useAlertStore
        .getState()
        .add({ label: 'Задача успешно удалена из планировщика', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось удалить задачу из планировщика', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
